import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Pencil, Wallet } from 'lucide-react'
import {
  useEmployee,
  useTerminateEmployee,
  useActivateEmployee,
  useSalaryPayments,
} from '@/hooks/useEmployees'
import { StatusBadge } from '@/components/ui/status-badge'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Button } from '@/components/ui/button'
import { maskCpf, maskPhone } from '@/components/form/masks'
import { formatBRL } from '@/lib/currency'
import { EMPLOYEE_ROLE_LABELS, type SalaryPayment } from '@/types/employee'
import { SalaryPaymentModal } from './SalaryPaymentModal'

function DetailRow({ label, value }: Readonly<{ label: string; value?: string | null }>) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{value || '—'}</dd>
    </div>
  )
}

function formatDate(iso: string | null) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('pt-BR')
}

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  corrente: 'Corrente',
  poupanca: 'Poupança',
}

export function EmployeeDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { data: employee, isLoading } = useEmployee(id ?? '')
  const { data: salaryPayments = [] } = useSalaryPayments(id ?? '')
  const terminate = useTerminateEmployee()
  const activate = useActivateEmployee()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [salaryModalOpen, setSalaryModalOpen] = useState(false)

  const isPending = terminate.isPending || activate.isPending

  async function handleToggle() {
    if (!employee) return
    try {
      if (employee.active) {
        await terminate.mutateAsync({ id: employee.id })
        toast.success('Funcionário desligado')
      } else {
        await activate.mutateAsync(employee.id)
        toast.success('Funcionário reativado')
      }
    } catch {
      toast.error('Erro ao alterar status do funcionário')
    } finally {
      setConfirmOpen(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Funcionário não encontrado.</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon-sm" onClick={() => navigate('/employees')}>
            <ArrowLeft size={16} />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-foreground">{employee.name}</h1>
              <StatusBadge active={employee.active} />
            </div>
            <p className="text-sm text-muted-foreground">
              {EMPLOYEE_ROLE_LABELS[employee.role]} · Admissão em {formatDate(employee.hireDate)}
              {employee.terminationDate && ` · Desligado em ${formatDate(employee.terminationDate)}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/employees/${employee.id}/edit`)}>
            <Pencil size={16} />
            Editar
          </Button>
          <Button onClick={() => setSalaryModalOpen(true)}>
            <Wallet size={16} />
            Registrar pagamento
          </Button>
          <Button
            variant={employee.active ? 'destructive' : 'outline'}
            onClick={() => setConfirmOpen(true)}
            disabled={isPending}
          >
            {employee.active ? 'Desligar' : 'Reativar'}
          </Button>
        </div>
      </div>

      <section className="rounded-lg border border-border bg-card p-6">
        <h2 className="mb-4 text-sm font-semibold text-foreground">Dados pessoais</h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DetailRow label="CPF" value={maskCpf(employee.cpf)} />
          <DetailRow label="Email" value={employee.email} />
          <DetailRow label="Telefone" value={employee.phone ? maskPhone(employee.phone) : null} />
          <DetailRow label="Cargo" value={EMPLOYEE_ROLE_LABELS[employee.role]} />
        </dl>
      </section>

      <section className="rounded-lg border border-border bg-card p-6">
        <h2 className="mb-4 text-sm font-semibold text-foreground">Remuneração</h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DetailRow label="Salário base" value={formatBRL(employee.baseSalary)} />
          <DetailRow label="Dia de pagamento" value={`Todo dia ${employee.paymentDay}`} />
        </dl>
      </section>

      {(employee.bankName || employee.pixKey) && (
        <section className="rounded-lg border border-border bg-card p-6">
          <h2 className="mb-4 text-sm font-semibold text-foreground">Dados bancários</h2>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DetailRow label="Banco" value={employee.bankName} />
            <DetailRow label="Agência" value={employee.bankAgency} />
            <DetailRow label="Conta" value={employee.bankAccount} />
            <DetailRow
              label="Tipo"
              value={
                employee.bankAccountType
                  ? ACCOUNT_TYPE_LABELS[employee.bankAccountType] ?? employee.bankAccountType
                  : null
              }
            />
            <DetailRow label="Chave PIX" value={employee.pixKey} />
          </dl>
        </section>
      )}

      <section className="rounded-lg border border-border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">Histórico de pagamentos</h2>
          <p className="text-xs text-muted-foreground">
            {salaryPayments.length} {salaryPayments.length === 1 ? 'pagamento' : 'pagamentos'}
          </p>
        </div>
        {salaryPayments.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Nenhum pagamento registrado ainda.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-xs text-muted-foreground">
                <tr>
                  <th className="py-2 text-left font-medium">Referência</th>
                  <th className="py-2 text-left font-medium">Data do pagamento</th>
                  <th className="py-2 text-right font-medium">Valor</th>
                </tr>
              </thead>
              <tbody>
                {salaryPayments.map((p: SalaryPayment) => (
                  <tr key={p.id} className="border-b border-border last:border-0">
                    <td className="py-2">
                      {String(p.referenceMonth).padStart(2, '0')}/{p.referenceYear}
                    </td>
                    <td className="py-2">{new Date(p.paymentDate).toLocaleDateString('pt-BR')}</td>
                    <td className="py-2 text-right font-medium tabular-nums">
                      {formatBRL(p.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <SalaryPaymentModal
        employee={employee}
        open={salaryModalOpen}
        onClose={() => setSalaryModalOpen(false)}
      />

      <ConfirmDialog
        open={confirmOpen}
        title={employee.active ? 'Desligar funcionário' : 'Reativar funcionário'}
        description={
          employee.active
            ? `${employee.name} será marcado como desligado a partir de hoje. O histórico de pagamentos é preservado.`
            : `${employee.name} voltará a aparecer como ativo. A data de desligamento será removida.`
        }
        confirmLabel={employee.active ? 'Desligar' : 'Reativar'}
        destructive={employee.active}
        loading={isPending}
        onConfirm={handleToggle}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  )
}
